import { Directive, ElementRef, OnDestroy, OnInit, Renderer2 } from '@angular/core';

// Every contact-number box in the app (20 forms) is a bare <input> sitting next to a country-code
// dropdown, with nothing telling the user what to type. Rather than hand-editing each template, this
// attaches to any <input formControlName="contactNo"> and gives them all the same guidance:
//  - an example placeholder (unless the form already has a specific one),
//  - a numeric keypad on phones,
//  - a short visible line under the field saying what to enter.
@Directive({ selector: 'input[formControlName="contactNo"]' })
export class PhoneNumberHintDirective implements OnInit, OnDestroy {
  // Just the digits: the box beside the dialing code is narrow, and the hint line below says "e.g.".
  static readonly EXAMPLE = '712345678';
  static readonly HINT = 'Enter the number without the country code, e.g. 712345678';
  // Placeholders that say nothing useful - replaced; anything more specific is left alone.
  private static readonly GENERIC = /^\s*(enter\s+)?(your\s+)?(contact|phone)(\s+(no|number))?\.?\s*$/i;

  private hint: HTMLElement | null = null;

  constructor(private el: ElementRef<HTMLInputElement>, private renderer: Renderer2) {}

  ngOnInit(): void {
    const input = this.el.nativeElement;
    const placeholder = input.getAttribute('placeholder');
    if (!placeholder || PhoneNumberHintDirective.GENERIC.test(placeholder)) {
      this.renderer.setAttribute(input, 'placeholder', PhoneNumberHintDirective.EXAMPLE);
    }
    this.renderer.setAttribute(input, 'inputmode', 'numeric');
    if (!input.getAttribute('autocomplete')) {
      this.renderer.setAttribute(input, 'autocomplete', 'tel-national');
    }
    this.renderer.setAttribute(input, 'aria-label', 'Phone number without the country code');

    // The number sits inside an input-group next to the dialing code; put the hint after the whole group.
    const anchor = (input.closest('.input-group') as HTMLElement) || input;
    const parent = anchor.parentNode;
    if (parent) {
      const hint = this.renderer.createElement('small') as HTMLElement;
      this.renderer.addClass(hint, 'vw-phone-hint');
      this.renderer.appendChild(hint, this.renderer.createText(PhoneNumberHintDirective.HINT));
      this.renderer.insertBefore(parent, hint, anchor.nextSibling);
      this.hint = hint;
    }
  }

  ngOnDestroy(): void {
    if (this.hint && this.hint.parentNode) {
      this.renderer.removeChild(this.hint.parentNode, this.hint);
    }
    this.hint = null;
  }
}
